import React, { useState, useEffect } from 'react';
import { GiChurch } from 'react-icons/gi';
import { LuLoader, LuUser, LuPhone, LuMail, LuMapPin } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { extractString, extractOrder } from '../../../shared/fieldUtils';

export default function FormerBishops({ activeSection, onSelectSection }) {
  const [bishops, setBishops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchBishops() {
      try {
        let list = [];

        // 1. Check lights-of-diocese-members collection
        let snap = await getDocs(collection(db, 'lights-of-diocese-members')).catch(() => null);
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'lights_of_diocese_members')).catch(() => null);
        }

        if (snap && !snap.empty) {
          list = snap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(item => {
              const t = extractString(item.type || item.category || item.designation || item.role).toLowerCase();
              return t.includes('retired bishop') || t.includes('retired-bishop') || t.includes('former bishop') || t.includes('former-bishop') || t.includes('pioneer') || (t.includes('bishop') && (t.includes('retired') || t.includes('former')));
            })
            .map(data => {
              const positionVal = typeof data.position === 'object' && data.position !== null ? data.position.value : extractString(data.position);
              const newVal = typeof data.new === 'object' && data.new !== null ? data.new.value : extractString(data.new);

              const period = extractString(data.period || data.years || data.term || data.duration || data.year)
                || (positionVal && positionVal !== 'NA' ? positionVal : '')
                || (newVal && newVal !== 'NA' ? newVal : '');

              return {
                id: data.id,
                name: extractString(data.name || data.bishop_name || data.title),
                period: period,
                desc: extractString(data.desc || data.description || data.bio || data.details),
                img_url: extractString(data.priestImg || data.img_url || data.image || data.photo),
                place: extractString(data.place || data.parish || data.location),
                phone: extractString(data.phoneno || data.phone || data.ph),
                email: extractString(data.email),
                order: extractOrder(data.order)
              };
            });
        }

        // 2. Check dedicated collections if empty
        if (list.length === 0) {
          let colSnap = await getDocs(collection(db, 'former-bishops')).catch(() => null);
          if (!colSnap || colSnap.empty) {
            colSnap = await getDocs(collection(db, 'former_bishops')).catch(() => null);
          }
          if (!colSnap || colSnap.empty) {
            colSnap = await getDocs(collection(db, 'former-bishop')).catch(() => null);
          }
          if (!colSnap || colSnap.empty) {
            colSnap = await getDocs(collection(db, 'formerbishops')).catch(() => null);
          }
          if (!colSnap || colSnap.empty) {
            colSnap = await getDocs(collection(db, 'pioneers')).catch(() => null);
          }
          if (!colSnap || colSnap.empty) {
            colSnap = await getDocs(collection(db, 'former-bishops-and-pioneers')).catch(() => null);
          }

          if (colSnap && !colSnap.empty) {
            list = colSnap.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                name: extractString(data.name || data.bishop_name || data.title),
                period: extractString(data.period || data.years || data.term || data.duration || data.year),
                desc: extractString(data.desc || data.description || data.bio || data.details),
                img_url: extractString(data.priestImg || data.img_url || data.image || data.photo),
                place: extractString(data.place || data.parish || data.location),
                phone: extractString(data.phoneno || data.phone || data.ph),
                email: extractString(data.email),
                order: extractOrder(data.order)
              };
            });
          }
        }

        list.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        if (isMounted) setBishops(list);
      } catch (err) {
        console.error("Error fetching former bishops:", err);
        if (isMounted) setBishops([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchBishops();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Header Section */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 sm:pb-20 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] sm:right-[10%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 text-center md:text-left flex flex-col md:items-start items-center">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full"></div>
          <h1 className="font-serif font-bold tracking-[0.03em] text-[#7B1E3A] text-[38px] sm:text-[50px] md:text-[58px] lg:text-[64px] leading-[1.05] uppercase">
            Former Bishops & Pioneers
          </h1>
          <p className="mt-4 sm:mt-5 max-w-xl text-[#7B6B66] text-[15px] sm:text-[17px] leading-[1.6] font-normal">
            Honoring the missionary legacy and the faithful episcopal pioneers of our Diocese.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 mt-16 w-full space-y-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-[#E8DDD7]">
            <LuLoader className="w-10 h-10 text-[#7B1E3A] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Loading Former Bishops...</p>
          </div>
        ) : bishops.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[24px] border border-[#E8DDD7] p-8 max-w-xl mx-auto">
            <h3 className="font-serif font-bold text-xl text-[#7B1E3A]">No Former Bishops Listed</h3>
            <p className="text-sm text-[#7B6B66] mt-1">Former bishops data is currently being populated from the administrative archives.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {bishops.map((bishop, idx) => (
              <div key={bishop.id || idx} className="bg-white p-6 rounded-[24px] border border-[#E8DDD7] shadow-[0_10px_30px_rgba(123,30,58,0.04)] group hover:-translate-y-1 transition-transform flex flex-col items-start text-left">
                {bishop.img_url ? (
                  <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-sm border border-[#E8DDD7] mb-4 shrink-0 bg-[#FAF6F2]">
                    <img 
                      src={bishop.img_url} 
                      alt={bishop.name} 
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="w-full h-full object-cover object-top" 
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 shrink-0 bg-[#FAF6F2] text-[#7B1E3A] rounded-full flex items-center justify-center group-hover:bg-[#7B1E3A] group-hover:text-white transition-colors mb-4">
                    <LuUser className="w-7 h-7" />
                  </div>
                )}
                <div className="w-full">
                  <h3 className="font-serif font-bold text-[#7B1E3A] text-lg mb-4">{bishop.name}</h3>
                  <div className="space-y-3 text-[#5F5A58] text-sm">
                    {bishop.period && bishop.period !== "NA" && (
                      <div className="flex items-start gap-2.5">
                        <span className="text-xs font-bold text-[#D4A64A] uppercase tracking-wider">{bishop.period}</span>
                      </div>
                    )}
                    {bishop.desc && bishop.desc !== "NA" && (
                      <p className="text-[#5F5A58] text-xs leading-relaxed">
                        {bishop.desc}
                      </p>
                    )}
                    {bishop.place && bishop.place !== "NA" && (
                      <div className="flex items-start gap-2.5">
                        <LuMapPin className="w-4 h-4 text-[#D4A64A] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{bishop.place}</span>
                      </div>
                    )}
                    {bishop.phone && bishop.phone !== "0" && bishop.phone !== "NA" && (
                      <div className="flex items-center gap-2.5">
                        <LuPhone className="w-4 h-4 text-[#D4A64A] shrink-0" />
                        <a href={`tel:${bishop.phone}`} className="leading-relaxed hover:text-[#7B1E3A] transition-colors">{bishop.phone}</a>
                      </div>
                    )}
                    {bishop.email && bishop.email !== "NA" && bishop.email !== "." && (
                      <div className="flex items-start gap-2.5">
                        <LuMail className="w-4 h-4 text-[#D4A64A] shrink-0 mt-0.5" />
                        <a href={`mailto:${bishop.email.trim()}`} className="hover:text-[#7B1E3A] transition-colors break-words whitespace-normal leading-relaxed">{bishop.email}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Navigation */}
        <div className="text-center pb-8 pt-8">
          <button 
            onClick={() => onSelectSection('lights')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#7B1E3A] hover:bg-[#5E132A] text-white font-sans font-bold text-sm tracking-wider uppercase rounded-full transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer"
          >
            Return to Lights of Diocese
          </button>
        </div>
      </div>
    </div>
  );
}
